import React, { useEffect, useState } from "react";
import { Modal, Button, Card, Row, Col, Image } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import { getDepartmentDetails } from "../../services/departmentService";
import { getFile } from "../../utils/getFile";
import config from "../../config";
import User from "../../assets/img/icons/user3.png";
import { useNavigate } from "react-router-dom";
import { encrypt } from "../../utils/crypto";

const DepartmentDetail = ({ show, handleClose, departmentId }) => {
  const [departmentDetail, setDepartmentDetail] = useState([]);
  const [imageMap, setImageMap] = useState({});
  const [loader, setLoader] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchImages = async () => {
      if (
        !departmentDetail?.employees &&
        !departmentDetail?.departmentAssignedHr
      ) {
        setImageMap({});
        return;
      }

      const employees = departmentDetail?.employees ?? [];
      const departmentAssignedHr = departmentDetail?.departmentAssignedHr ?? [];
      const allEmployees = [...employees, ...departmentAssignedHr];

      const imagePromises = allEmployees
        .filter((employee) => employee?.employee_photo || employee?.hr_photo)
        .map(async (employee) => {
          setLoader(true);
          try {
            const imageUrl = await getFile(
              `${config?.IMAGE_URL}${
                employee.employee_photo ?? employee.hr_photo
              }`
            );
            return {
              id: employee.employee_id ?? employee.hr_employee_id,
              imageUrl,
            };
          } catch (error) {
            console.error(
              `Failed to fetch image for employee ${
                employee.employee_id ?? employee.hr_employee_id
              }:`,
              error
            );
            return {
              id: employee.employee_id ?? employee.hr_employee_id,
              imageUrl: null,
            };
          } finally {
            setLoader(false);
          }
        });

      const imageResults = await Promise.all(imagePromises);
      const newImageMap = imageResults.reduce((map, { id, imageUrl }) => {
        if (imageUrl && id) map[id] = imageUrl;
        return map;
      }, {});

      setImageMap(newImageMap);
    };

    fetchImages().catch((error) => {
      console.error("Error in fetchImages:", error);
      setImageMap({});
    });
  }, [departmentDetail]);

  const fetchDepartmentDetails = async (id) => {
    setLoader(false);
    try {
      const response = await getDepartmentDetails(id);
      setDepartmentDetail(response?.details);
    } catch (error) {
      console.error(error);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    if (departmentId) {
      fetchDepartmentDetails(departmentId);
    }
  }, []);

  return (
    <Modal show={show} backdrop="static" size="lg" centered>
      <Modal.Header>
        <Modal.Title>Department Details</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-4">
        {loader ? (
          <div className="text-center">
            <div className="spinner-border text-primary" role="status"></div>
            <p className="">Loading...</p>
          </div>
        ) : departmentDetail?.employees?.length ||
          departmentDetail?.departmentAssignedHr?.length ? (
          <>
            <section className="mb-4">
              <h4 className="mb-3">Assigned HR</h4>
              {departmentDetail?.departmentAssignedHr?.map((hr) => {
                const encryptedEmployeeId = encrypt(hr?.hr_employee_id);
                return (
                  <Card
                    key={hr?.hr_employee_id}
                    className="mb-3 shadow-sm"
                    onClick={() => {
                      if (hr?.hr_employee_id) {
                        navigate(`../employees/profile/${encryptedEmployeeId}`);
                      }
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    <Card.Body className="d-flex align-items-center">
                      <div className="me-3">
                        {hr?.hr_photo ? (
                          <Image
                            src={imageMap[hr?.hr_employee_id] ?? User}
                            alt="HR Photo"
                            roundedCircle
                            width={50}
                            height={50}
                            style={{ objectFit: "cover" }}
                          />
                        ) : (
                          <div
                            className="rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center"
                            style={{
                              width: 50,
                              height: 50,
                              fontSize: "1.5rem",
                            }}
                          >
                            {hr?.hr_first_name ? hr?.hr_first_name[0] : "NA"}
                          </div>
                        )}
                      </div>
                      <div>
                        <Card.Title className="mb-1">
                          {hr?.hr_first_name}
                          {hr?.hr_middle_name ? ` ${hr?.hr_middle_name} ` : " "}
                          {hr?.hr_last_name}
                        </Card.Title>
                        <Card.Text className="text-muted small">
                          ID: {hr?.hr_employment_id ?? "-"}
                        </Card.Text>
                      </div>
                    </Card.Body>
                  </Card>
                );
              })}
            </section>
            <section>
              <h4 className="mb-3">Employees</h4>
              <Row>
                {departmentDetail?.employees?.map((employee) => {
                  const encryptedEmployeeId = encrypt(employee?.employee_id);
                  return (
                    <Col
                      md={6}
                      key={employee?.employee_id}
                      className="mb-3"
                      onClick={() => {
                        if (employee?.employee_id) {
                          navigate(
                            `../employees/profile/${encryptedEmployeeId}`
                          );
                        }
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      <Card className="shadow-sm h-100">
                        <Card.Body className="d-flex align-items-center">
                          <div className="me-3">
                            {employee?.employee_photo ? (
                              <Image
                                src={imageMap[employee?.employee_id] ?? User}
                                alt="Employee Photo"
                                roundedCircle
                                width={50}
                                height={50}
                                style={{ objectFit: "cover" }}
                              />
                            ) : (
                              <div
                                className="rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center"
                                style={{
                                  width: 50,
                                  height: 50,
                                  fontSize: "1.5rem",
                                }}
                              >
                                {employee?.employee_first_name
                                  ? employee?.employee_first_name[0]
                                  : "NA"}
                              </div>
                            )}
                          </div>
                          <div>
                            <Card.Title className="mb-1">
                              {employee?.employee_first_name}
                              {employee?.employee_middle_name
                                ? ` ${employee?.employee_middle_name} `
                                : " "}
                              {employee?.employee_last_name}
                            </Card.Title>
                            <Card.Text className="text-muted small">
                              {employee?.department_name || "No Designation"} •{" "}
                              {employee?.designation_name}
                            </Card.Text>
                            <Card.Text className="text-muted small">
                              ID: {employee?.employment_id ?? "-"}
                            </Card.Text>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            </section>{" "}
          </>
        ) : (
          <div className="text-center">No Data</div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={handleClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DepartmentDetail;
